import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BugReport, Settings } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

import { Character } from '@dndModels';

import CheckBox from "@components/input/CheckBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FlexBox from '@components/layout/FlexBox';
import DandDAPI from '@dndUtilities/system_api';
import CenteredMenu from '@components/input/menus/CenteredMenu';
import InfoButton from '@components/input/buttons/InfoButton';
import CharacterStatsCustomizations from './CharacterStatsCustomizations';
import CharacterCreatureTypeCustomization from './CharacterCreatureTypeCustomization';
import CharacterSizeCustomizations from './CharacterSizeCustomizations';
import CharacterPBCustomizations from './CharacterPBCustomizations';
import CharacterSpeedCustomizations from './CharacterSpeedCustomizations';
import CharacterInitCustomizations from './CharacterInitCustomizations';
import CharacterACCustomizations from './CharacterACCustomizations';
import CharacterMaxHPCustomizations from './CharacterMaxHPCustomizations';
import CharacterSavingThrowCustomizations from './CharacterSavingThrowCustomizations';

interface Props {
  char: Character;
}

const api = DandDAPI.getInstance();

export default function CharacterSettings({
  char
}: Props) {
  const [open, setOpen] = useState(false);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [view, setView] = useState("Character Settings");
  const [display_info, setDisplayInfo] = useState(api.user ? !api.user.hide_info.includes("Character Customizations") : true);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const recalculated = useCallback(() => {
    console.log("Char Race Button - Modal Closed");
    setReloading(true);
  }, []);

  useEffect(() => {
    char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    char.subscription_holder.subscribe("Resources", mounted, recalculated);
  }, [char, recalculated]);

  const renderView = () => {
    switch (view) {
      case "Character Settings": return renderCharacterSettings();
      case "Customizations": return renderCustomizations();
    }
    return null;
  }

  const renderCharacterSettings = () => {
    return (
      <>
        { char.c_char &&
          <FlexBox direction="row">
            <FlexBox>
              <CheckBox
                name="Hide Character from other Players in Campaign"
                value={char.c_char.hide_char}
                onChange={ async (changed) => {
                  if (char.c_char) {
                    char.c_char.hide_char = changed;
                    await api.upsertObject("campaign_char", char.c_char);
                    setPing(!ping);
                  }
                }}
              />
            </FlexBox>
            <FlexBox>
              <CheckBox
                name="Hide Details from other Players in Campaign"
                value={char.c_char.hide_details}
                onChange={ async (changed) => {
                  if (char.c_char) {
                    char.c_char.hide_details = changed;
                    await api.upsertObject("campaign_char", char.c_char);
                    setPing(!ping);
                  }
                }}
              />
            </FlexBox>
          </FlexBox>
        }
        <FlexBox direction="row">
          <FlexBox>
            <CheckBox
              name="Show Hidden"
              value={char.show_hidden}
              onChange={(changed) => {
                char.show_hidden = changed;
                char.subscription_holder.displayChanged();
                setPing(!ping);
              }}
            />
          </FlexBox>
          <FlexBox>
            <CheckBox
              name="Show Extra Action Types"
              value={char.show_extra_action_types}
              onChange={(changed) => {
                char.show_extra_action_types = changed;
                char.subscription_holder.displayChanged();
                setPing(!ping);
              }}
            />
          </FlexBox>
          <FlexBox>
            <CheckBox
              name="Show Rituals"
              value={char.show_rituals}
              onChange={(changed) => {
                char.show_rituals = changed;
                char.subscription_holder.displayChanged();
                setPing(!ping);
              }}
            />
          </FlexBox>
        </FlexBox>
      </>
    );
  }

  const toggleInfo = () => {
    const user = api.user;
    if (user) {
      if (display_info) {
        user.hide_info.push("Character Customizations");
      } else {
        user.hide_info = user.hide_info.filter(o => o !== "Character Customizations");
      }

      api.upsertObject("user", user);
    }
    setDisplayInfo(!display_info);
  }

  const renderCustomizations = () => {
    return (
      <>
        <FlexBox>
          <InfoButton onClick={() => toggleInfo() } />
        </FlexBox>
        { display_info && 
          <>
            <FlexBox>WARNING! This Feature is not yet fully implemented, so this info may not be completely accurate.</FlexBox>
            <FlexBox>
              Customizations either upgrade or override things in your character sheet. 
              This can be very powerful, and should never be done without your GM's permission, 
              which is why we have made it so they can only be used if the Character Options 
              on your Campaign allow you to do so. 
              The GM can choose to make it so only they can do customizations, 
              and then they may give you customizations that you can't give yourself.
            </FlexBox>
            <FlexBox>
              Many customizations are also available in controls for the things they're tied to.
              We don't have all customization controls here, but there are more here than anywhere else.
              The ones that are not found here are about specific things, 
              like customizing a specific ability or spell. 
              The customizations for those kinds of things would be in the controls for that particular thing.
              If there is something you want to customize, but don't see a way to do so, 
              it's probably a customization that we haven't gotten to yet. 
              You can click the <BugReport /> on the bar at the top of the page, and use the tool there to submit a request.
              We get to all requests as soon as we can, but we are a small team, so please be patient.
            </FlexBox>
            <FlexBox>
              One type of customization in particular that will only be available here is Custom Abilities.
              These are complicated, as they allow you to define how the ability works, 
              using tools similar to those we use for defining how the various features granted by your 
              Species, Background, Class(es), Feats, etc. work. 
              It's inherently more complicated, but it can do all kinds of things, so it may be worth it.
            </FlexBox>
          </>
        }
        { renderStatCustomizations() }
        <FlexBox><hr /></FlexBox>
        { renderSavingThrowCustomizations() }
        <FlexBox><hr /></FlexBox>
        { renderSpeedCustomizations() }
        <FlexBox><hr /></FlexBox>
        <CharacterMaxHPCustomizations char={char} />
        <FlexBox><hr /></FlexBox>
        <CharacterCreatureTypeCustomization char={char} />
        <CharacterSizeCustomizations char={char} />
        <CharacterPBCustomizations char={char} />
        <CharacterInitCustomizations char={char} />
        <CharacterACCustomizations char={char} />
      </>
    );
  }

  // Saves
  // Skills
  // Senses
  // Damage Multipliers
  // Condition Immunities
  // Armor Training
  // Weapon Profs and Masteries
  // Tool Profs
  // Resources

  const renderStatCustomizations = () => {
    return (
      <>
        <FlexBox className="header centered">
          Ability Scores
        </FlexBox>
        <FlexBox>
          <CharacterStatsCustomizations char={char} />
        </FlexBox>
      </>
    );
  }

  const renderSavingThrowCustomizations = () => {
    return (
      <>
        <FlexBox className="header centered">
          Saving Throws
        </FlexBox>
        <FlexBox>
          <CharacterSavingThrowCustomizations char={char} />
        </FlexBox>
      </>
    );
  }

  const renderSpeedCustomizations = () => {
    return (
      <>
        <FlexBox className="header centered">
          Speeds
        </FlexBox>
        <FlexBox>
          <CharacterSpeedCustomizations char={char} />
        </FlexBox>
      </>
    );
  }

  const renderCustomAbilities = () => {
    return (
      <>
      </>
    );
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={open}
        onClose={() => { setOpen(false) }}
      >
        <FlexBox direction="column">
          <FlexBox>
            <CenteredMenu options={["Character Settings","Customizations"]} selected={view} onSelect={setView} />
          </FlexBox>
          { renderView() }
        </FlexBox>
      </ModalOrDrawer>
    );
  }

  return (
    <>
      <Tooltip title={`Character Settings`}>
        <Settings 
          className="clickable"
          onClick={() => { setOpen(true); }} 
        />
      </Tooltip>
      { renderExtras() }
    </>
  ); 
}

